import type { UseQueryOptions } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { generalConfig, noCacheConfig } from "../config/general"
import QUERY_KEYS from "../constants/queryKeys"
import { secondsToMilliseconds } from "date-fns"
import { StatusFilter } from "../enums/markets"
// Using native JavaScript sort instead of lodash
import type { League } from "overtime-utils"
import { LeagueMap } from "overtime-utils"
import type { MarketsCache, TicketPosition } from "../types/markets"
import type { NetworkConfig } from "../types/network"
import { packMarket } from "../utils/marketsV2"
import { OVERTIME_API_KEY } from "@/config/env"
import { useLive } from "@azuro-org/sdk"
import useChainProviderHook from "@/hooks/useChainProvider"

const overtimeApiConfig = {
  ...noCacheConfig,
  headers: {
    ...noCacheConfig.headers,
    "x-api-key": OVERTIME_API_KEY,
  },
}

const marketsCache: MarketsCache = {
  [StatusFilter.OPEN_MARKETS]: [],
  [StatusFilter.ONGOING_MARKETS]: [],
  [StatusFilter.RESOLVED_MARKETS]: [],
  [StatusFilter.PAUSED_MARKETS]: [],
  [StatusFilter.CANCELLED_MARKETS]: [],
}

const useSportsMarketsV2Query = (
  statusFilter: StatusFilter,
  includeProofs: boolean,
  networkConfig: NetworkConfig,
  ticket?: TicketPosition[],
  options?: Omit<UseQueryOptions<any>, "queryKey" | "queryFn">
) => {
  const { isLive } = useLive()
  const { isOvertimeChain } = useChainProviderHook()
  const gameIds = ticket?.map(market => market.gameId).join(",") || ""
  const typeIds = ticket?.map(market => market.typeId).join(",") || ""
  const playerIds = ticket?.map(market => market.playerId).join(",") || ""
  const lines = ticket?.map(market => market.line).join(",") || ""

  return useQuery<MarketsCache>({
    queryKey: QUERY_KEYS.SportMarketsV2(
      statusFilter,
      networkConfig.networkId,
      includeProofs,
      gameIds,
      typeIds,
      playerIds,
      lines
    ),
    queryFn: async () => {
      try {
        const status = statusFilter.toLowerCase().split("market")[0]
        const today = new Date()
        const minMaturity = Math.round(
          new Date(new Date().setDate(today.getDate() - 7)).getTime() / 1000
        )

        const maxMaturity = Math.round(
          new Date(new Date().setDate(today.getDate() + 7)).getTime() / 1000
        )

        const fetchLiveScore = statusFilter === StatusFilter.ONGOING_MARKETS
        const fetchGameInfo =
          statusFilter === StatusFilter.ONGOING_MARKETS ||
          statusFilter === StatusFilter.RESOLVED_MARKETS

        const [marketsResponse, gamesInfoResponse, liveScoresResponse] =
          await Promise.all([
            axios.get(
              `${generalConfig.API_URL}/overtime-v2/networks/${
                networkConfig.networkId
              }/markets?ungroup=true&onlyBasicProperties=true&includeProofs=${includeProofs}${
                ticket
                  ? ""
                  : `&minMaturity=${minMaturity}&maxMaturity=${maxMaturity}&onlyMainMarkets=true`
              }${ticket ? `&gameIds=${gameIds}` : ""}${ticket ? `&typeIds=${typeIds}` : ""}${
                ticket ? `&playerIds=${playerIds}` : ""
              }${ticket ? `&lines=${lines}` : ""}`,
              overtimeApiConfig
            ),
            fetchGameInfo
              ? axios.get(
                  `${generalConfig.API_URL}/overtime-v2/games-info`,
                  overtimeApiConfig
                )
              : undefined,
            fetchLiveScore
              ? axios.get(
                  `${generalConfig.API_URL}/overtime-v2/live-scores`,
                  overtimeApiConfig
                )
              : undefined,
          ])

        const markets = marketsResponse.data
        const gamesInfo = gamesInfoResponse?.data
        const liveScores = liveScoresResponse?.data

        const mappedMarkets = markets
          .filter(
            (market: any) => !LeagueMap[market.leagueId as League]?.hidden
          )
          .map((market: any) => {
            const gameInfo = gamesInfo ? gamesInfo[market.gameId] : undefined
            const liveScore = liveScores ? liveScores[market.gameId] : undefined

            return {
              ...packMarket(market, gameInfo, liveScore, false),
              childMarkets: market.childMarkets
                .filter(
                  (childMarket: any) => market.status === childMarket.status
                )
                .map((childMarket: any) =>
                  packMarket(childMarket, gameInfo, liveScore, false, market)
                )
                .sort((a: any, b: any) => a.typeId - b.typeId),
            }
          })

        marketsCache[statusFilter] = mappedMarkets

        return { ...marketsCache, [statusFilter]: mappedMarkets }
      } catch (e) {
        console.log(e)
      }
      return marketsCache
    },

    refetchInterval: secondsToMilliseconds(20),
    enabled: !isLive && isOvertimeChain && (options?.enabled ?? true),
    ...options,
  })
}

export default useSportsMarketsV2Query
